import { ref } from 'vue'
import type { Mixpanel } from 'mixpanel-browser/dist/mixpanel-core.cjs'
import { isInsidePiBrowser } from '@/services/piDetection'

/**
 * Product analytics through Mixpanel (EU data residency). Off entirely without
 * VITE_MIXPANEL_TOKEN. With a token, the SDK is not even downloaded until the
 * visitor opts in, and nothing is sent after they opt out. Event names and what
 * they mean for marketing: docs/analytics.md.
 */

/** Every event the app sends, with its properties. Add new ones here first. */
export interface AnalyticsEvents {
  'Page Viewed': { page: string; path: string; search?: string }
  'Signed In': { method: 'pi-browser' | 'pi-signin'; new_user: boolean }
  'Wallet Linked': Record<string, never>
  'Listing Published': { listing_id: string; category_id: string; price_pi: number }
  'Order Placed': { order_id: string; listing_id: string; amount_pi: number }
  'Payment Completed': { order_id: string; amount_pi: number }
  'Delivery Accepted': { order_id: string; amount_pi: number }
  'Link Shared': { target: 'listing' | 'storefront'; result: 'shared' | 'copied' }
}

export type AnalyticsConsent = 'granted' | 'denied'

const CONSENT_KEY = 'settings:analytics'
const TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']

/** Whether this build reports analytics at all (a token is configured). */
export const analyticsAvailable = !!TOKEN

/** The visitor's answer, or null while they haven't answered the banner yet. */
export const analyticsConsent = ref<AnalyticsConsent | null>(readConsent())

const CAMPAIGN_KEY = 'ts_landing_campaign'

// The campaign link a visitor arrived on. Read at boot, because by the time they
// opt in the app may have navigated away and the SDK would miss it. Kept for the
// tab, so the full-page Pi Sign-in round trip doesn't lose it.
const landingCampaign = readLandingCampaign()

type QueuedEvent = { [Name in keyof AnalyticsEvents]: { name: Name; properties: AnalyticsEvents[Name] } }[keyof AnalyticsEvents]

// Sent if the visitor taps Allow on this page load: the page they are on, and a
// sign-in that happened before they answered the banner.
let queued: QueuedEvent[] = []

let client: Promise<Mixpanel | null> | null = null
let userID: string | null = null

function readConsent(): AnalyticsConsent | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY)
    return value === 'granted' || value === 'denied' ? value : null
  } catch {
    return null
  }
}

function readLandingCampaign(): Record<string, string> {
  const params = new URLSearchParams(window.location.search)
  const campaign = Object.fromEntries(
    UTM_KEYS.flatMap((key) => {
      const value = params.get(key)
      return value ? [[key, value]] : []
    }),
  )
  if (!analyticsAvailable || analyticsConsent.value === 'denied') return campaign
  try {
    if (Object.keys(campaign).length > 0) sessionStorage.setItem(CAMPAIGN_KEY, JSON.stringify(campaign))
    else return JSON.parse(sessionStorage.getItem(CAMPAIGN_KEY) ?? '{}')
  } catch {
    // Storage blocked: only this page load knows the campaign.
  }
  return campaign
}

/** Removes everything Mixpanel stored (`mp_*`, `__mp*`), so an opt-out leaves no trace. */
function clearStoredData() {
  try {
    sessionStorage.removeItem(CAMPAIGN_KEY)
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('mp_') || key.startsWith('__mp')) localStorage.removeItem(key)
    }
  } catch {
    // Storage blocked: nothing was stored either.
  }
}

// Opting in again after an opt-out reuses the instance: Mixpanel inits once per page.
// Resolves to null when the SDK can't load (an ad blocker), and every call is then a no-op.
function start(): Promise<Mixpanel | null> {
  client ??= import('mixpanel-browser/dist/mixpanel-core.cjs').then(({ default: mixpanel }) => {
    mixpanel.init(TOKEN!, {
      api_host: 'https://api-eu.mixpanel.com',
      persistence: 'localStorage',
      autocapture: false,
      // We send our own page views: the SDK's would carry the full URL, and the
      // Pi Sign-in callback URL holds an access token.
      track_pageview: false,
      property_blacklist: ['$current_url'],
      // landingCampaign is the one source of utm_* super properties: the SDK's own
      // copy would come from whatever URL is open when the visitor opts in.
      store_google: false,
    })
    return mixpanel
  }).catch(() => {
    client = null
    return null
  })
  return client.then((mixpanel) => {
    if (!mixpanel) return null
    // A no-op track skips the $opt_in event the SDK would send.
    mixpanel.opt_in_tracking({ track: () => {} })
    // A new campaign link replaces the stored one as a whole, so no utm_* value
    // from an earlier campaign sticks to it.
    if (Object.keys(landingCampaign).length > 0) {
      for (const key of UTM_KEYS) if (!(key in landingCampaign)) mixpanel.unregister(key)
    }
    mixpanel.register({ pi_browser: isInsidePiBrowser(), ...landingCampaign })
    if (userID) mixpanel.identify(userID)
    for (const event of queued.splice(0)) mixpanel.track(event.name, event.properties)
    return mixpanel
  })
}

/** Starts analytics on boot when the visitor opted in on an earlier visit. */
export function initAnalytics() {
  if (!analyticsAvailable) return
  if (analyticsConsent.value === 'granted') void start()
  // The SDK writes its send queue asynchronously, so a write can land just after
  // an opt-out cleared it. The next page load removes what it left.
  else if (analyticsConsent.value === 'denied') clearStoredData()
}

export async function setAnalyticsConsent(consent: AnalyticsConsent) {
  analyticsConsent.value = consent
  try {
    localStorage.setItem(CONSENT_KEY, consent)
  } catch {
    // Private mode: the answer holds for this tab only.
  }
  if (!analyticsAvailable) return

  if (consent === 'granted') {
    await start()
    return
  }
  queued = []
  // Opting out clears the stored device id and super properties by default.
  const mixpanel = await client
  // No delete_user: we keep no Mixpanel profiles, and it would queue one more request.
  mixpanel?.opt_out_tracking({ delete_user: false })
  clearStoredData()
}

/** Ties events to an account by its internal id (never the Pi username), or forgets it on logout. */
export function identifyUser(id: string | null) {
  if (id === userID) return
  const previous = userID
  userID = id
  if (!client) return
  void client.then((mixpanel) => {
    if (id) mixpanel?.identify(id)
    else if (previous) mixpanel?.reset()
  })
}

export function track<Name extends keyof AnalyticsEvents>(name: Name, properties: AnalyticsEvents[Name]) {
  if (!analyticsAvailable) return
  if (analyticsConsent.value === null) return queue({ name, properties } as QueuedEvent)
  if (!client || analyticsConsent.value !== 'granted') return
  void client.then((mixpanel) => mixpanel?.track(name, properties))
}

/** Keeps the latest page view and any sign-in until the visitor answers the banner. */
function queue(event: QueuedEvent) {
  if (event.name === 'Page Viewed') queued = [...queued.filter((e) => e.name !== 'Page Viewed'), event]
  else if (event.name === 'Signed In') queued.push(event)
}
